import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from "web-vitals";
import { supabase } from "@/integrations/supabase/client";
import { getClientEnv } from "@/lib/clientEnv";
import { getSessionId } from "@/lib/sessionId";

const queue: Metric[] = [];
let flushTimer: number | null = null;
let circuitOpenUntil = 0;

const WEB_VITALS_PAUSE_KEY = "vs_web_vitals_pause_until_ms";
const CIRCUIT_COOLDOWN_MS = 5 * 60_000;

const pauseUntil = (): number => {
  try {
    return parseInt(sessionStorage.getItem(WEB_VITALS_PAUSE_KEY) || "0", 10) || 0;
  } catch {
    return 0;
  }
};

const openCircuit = () => {
  const until = Date.now() + CIRCUIT_COOLDOWN_MS;
  circuitOpenUntil = until;
  try {
    sessionStorage.setItem(WEB_VITALS_PAUSE_KEY, String(until));
  } catch {
    // Ignore storage failures; vitals must never affect UX.
  }
};

const isAutomationContext = (): boolean => {
  if (navigator.webdriver) return true;
  const params = new URLSearchParams(window.location.search);
  for (const key of params.keys()) {
    if (key.startsWith("codex_") || key.startsWith("playwright_")) return true;
  }
  return false;
};

const shouldSkipVitals = (): boolean => {
  if (typeof window === "undefined") return true;
  if (window.location.pathname.startsWith("/admin")) return true;
  if (navigator.onLine === false) return true;
  if (isAutomationContext()) return true;
  const now = Date.now();
  return now < circuitOpenUntil || now < pauseUntil();
};

const flush = async () => {
  if (queue.length === 0) return;
  if (shouldSkipVitals()) {
    queue.length = 0;
    return;
  }
  const batch = queue.splice(0, queue.length);

  try {
    const sessionId = getSessionId();
    const userId = (await supabase.auth.getUser()).data.user?.id ?? null;
    const env = getClientEnv();

    const rows = batch.map((m) => ({
      metric_name: m.name,
      value: Math.round(m.value * 100) / 100,
      rating: m.rating,
      page_url: location.pathname.slice(0, 500),
      user_agent: navigator.userAgent.slice(0, 500),
      connection_type: env.effective_type,
      device_type: env.device_type,
      session_id: sessionId,
      user_id: userId,
    }));

    const { error } = await supabase.from("web_vitals").insert(rows);
    if (error) openCircuit();
  } catch {
    openCircuit();
  }
};

const enqueue = (m: Metric) => {
  queue.push(m);
  if (flushTimer) window.clearTimeout(flushTimer);
  flushTimer = window.setTimeout(flush, 1500);
};

let started = false;
export const initWebVitals = () => {
  if (started || shouldSkipVitals()) return;
  started = true;
  onCLS(enqueue);
  onFCP(enqueue);
  onINP(enqueue);
  onLCP(enqueue);
  onTTFB(enqueue);
  // Final flush on unload
  addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
};
